import * as React from 'react'
import type { NetworkStatus } from '@apollo/client'
import { debounce } from 'lodash'
import { up } from 'styled-breakpoints'
import styled from 'styled-components'
import { useFlag } from 'toggled'

import { Catalog, Loader } from '~/components'
import { Filters } from '~/components/filters'
import { useFilters } from '~/hooks'
import { useFiltersState } from '~/hooks/use-filters-state'
import type { IProduct } from '~/types/products'
import { flags } from '~/utils/constants'

import { ContentLayout } from '../content-layout'

export interface ContentDesktopProps {
  products: IProduct[]
  networkStatus: NetworkStatus
  paginationInfo: IPagination
  paginationType?: 'infinite' | 'pages'
  onFetchMoreProducts: (page: number) => void
}

const StyledContainer = styled.div<{ $hasFiltersPanel: boolean }>`
  display: grid;
  gap: ${props => props.theme.spacing.comfortable}px;
  grid-template-columns: ${props => (props.$hasFiltersPanel ? `${props.theme.spacing.cozy * 35}px 1fr` : '1fr')};
  margin-top: ${props => props.theme.spacing.cozy}px;
  padding-left: ${props => props.theme.spacing.comfortable}px;
  padding-right: ${props => props.theme.spacing.comfortable}px;
  width: 100%;
`

const StyledCatalog = styled(Catalog)<{ $showCartInMainLayout: boolean }>`
  width: 100%;

  & > div {
    display: grid;
    gap: ${props => props.theme.spacing.comfortable}px;
    grid-template-columns: 1fr;
    padding: 0;

    ${up('lg')} {
      grid-template-columns: ${props => (props.$showCartInMainLayout ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)')};
    }

    ${up('xl')} {
      grid-template-columns: ${props => (props.$showCartInMainLayout ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)')};
    }
  }
`

const StyledFilters = styled(Filters)`
  padding-bottom: ${props => props.theme.spacing.cozy / 2}px;
`

export function ContentDesktop(props: ContentDesktopProps) {
  const { networkStatus, onFetchMoreProducts, paginationInfo, paginationType = 'infinite', products } = props

  const { noFiltersAvailable } = useFiltersState()

  const { isLoading } = useFilters()

  const showCartInMainLayout = useFlag(flags.SHOW_CART_IN_MAIN_LAYOUT)

  if (isLoading) {
    return <Loader />
  }

  return (
    <StyledContainer $hasFiltersPanel={!noFiltersAvailable}>
      {!noFiltersAvailable && (
        <div>
          <StyledFilters />
        </div>
      )}
      <ContentLayout
        dataSource={products}
        networkStatus={networkStatus}
        render={() => (
          <StyledCatalog
            products={products}
            $showCartInMainLayout={showCartInMainLayout}
            isStandalone
            paginationType={paginationType}
            pageInfo={paginationInfo}
            pagination={
              paginationType === 'infinite'
                ? {
                    pageStart: paginationInfo.page ?? 1, // need to specify correct page from pagination info when the user has navigated to another page and then goes back and continue scrolling to avoid loading duplicated products
                    hasMore: paginationInfo.hasNextPage,
                    initialLoad: true,
                    loadMore: debounce(onFetchMoreProducts, 500), // When the debounce is removed, the package requests page 2 and 3 when it should only request page 2
                  }
                : undefined
            }
          />
        )}
      />
    </StyledContainer>
  )
}

export default ContentDesktop
